import { useCallback, useEffect, useState } from 'react'

import { useQuery } from '@apollo/react-hooks'

import {
  KycStatus,
  LinkedAccount,
  LinkedAccountExternalSourceEnum,
  SpendAccount,
} from 'generated/types'

import FundingInitiated from './gql/funding-initiated-query.gql'

const POLLING_FREQUENCY = 1000
const POLLING_DURATION = 30000
const RETRY_TIMEOUT = 3000

type OnResolveCardStatus = () => void
type OnKYCFail = () => void

export type FundingInitiatedQuery = {
  spendAccount: SpendAccount
  linkedAccounts: [LinkedAccount]
}

const checkingAccountNotReady = (spendAccount: SpendAccount) => {
  const kycStatus = spendAccount?.bankCardOrder?.kycStatus
  const accountId = spendAccount?.accountId
  /**
   * There's a delay between the time kyc status is accepted (IDology) & when we create the checking account (CorePro)
   *
   * In this case we want to treat the user as if they soft failed, since
   * eventually the checking account will be created
   *
   * See: https://acorns.slack.com/archives/C03825AU8KE/p1668034150589579?thread_ts=1668031202.595369&cid=C03825AU8KE
   */
  return kycStatus === KycStatus.Accepted && !accountId
}

const isKYCSoftFail = (spendAccount?: SpendAccount) => {
  const kycStatus = spendAccount?.bankCardOrder?.kycStatus
  return [KycStatus.ActionRequired, KycStatus.Pending].includes(kycStatus)
}

const isKYCHardFail = (spendAccount?: SpendAccount) => {
  const kycStatus = spendAccount?.bankCardOrder?.kycStatus
  return kycStatus === KycStatus.Failure
}

export const shouldTreatAsSoftFail = (data?: FundingInitiatedQuery): boolean => {
  const spendAccount = data?.spendAccount
  const linkedAccounts = data.linkedAccounts
  return (
    hasManualFundingSource(linkedAccounts) ||
    isKYCSoftFail(spendAccount) ||
    checkingAccountNotReady(spendAccount)
  )
}

/**
 * Users with manual funding sources need to verify RDVs before having account created.
 * They're not able make deposits yet so we treat them as soft fail as well
 */
const hasManualFundingSource = (linkedAccounts: LinkedAccount[] = []): boolean => {
  return linkedAccounts.some(
    account => account.externalSource === LinkedAccountExternalSourceEnum.Manual,
  )
}

const useFundingInitiatedQuery = () =>
  useQuery<FundingInitiatedQuery>(FundingInitiated, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
    notifyOnNetworkStatusChange: true,
  })

export const useFundingInitiatedPolling = (onResolve: OnResolveCardStatus) => {
  const { data, loading, startPolling: poll, stopPolling } = useFundingInitiatedQuery()
  const [timer, setTimer] = useState<NodeJS.Timeout>(null)
  const [isPolling, setIsPolling] = useState(false)
  const fundingInitiated = data?.spendAccount.bankCardOrder?.fundingInitiated

  const resolve = useCallback(() => {
    stopPolling()
    setIsPolling(false)
    onResolve()
  }, [onResolve, stopPolling])

  const startPolling = useCallback(() => {
    setIsPolling(true)
    poll(POLLING_FREQUENCY)

    if (!timer) {
      const firstTimer = setTimeout(() => {
        stopPolling()
        resolve()
      }, POLLING_DURATION)

      setTimer(firstTimer)
    }
  }, [poll, stopPolling, timer, resolve])

  useEffect(() => {
    if (fundingInitiated) {
      resolve()
    }
  }, [fundingInitiated, stopPolling, resolve])

  return {
    startPolling,
    isPolling,
    loading,
  }
}

interface KYCFailRedirectProps {
  onSoftFail: OnKYCFail
  onHardFail: OnKYCFail
}

export const useKYCFailRedirect = ({ onSoftFail, onHardFail }: KYCFailRedirectProps) => {
  const { data, loading, refetch } = useFundingInitiatedQuery()
  const [isRefetching, setIsRefetching] = useState(false)
  const isSoftFail = isKYCSoftFail(data?.spendAccount)
  const isHardFail = isKYCHardFail(data?.spendAccount)
  const isWaitingForCheckingReady = checkingAccountNotReady(data?.spendAccount)
  const isLoading = loading || isRefetching

  /**
   * User with kfc hard fail
   */
  useEffect(() => {
    if (isHardFail && onHardFail) {
      onHardFail()
    }
  }, [isHardFail, onHardFail])

  /**
   * User with kfc soft fail
   */
  useEffect(() => {
    if (isSoftFail && onSoftFail) {
      onSoftFail()
    }
  }, [isSoftFail, onSoftFail])

  /**
   * If the user passed kyc verification but the account id is not ready yet,
   * wait a few seconds and retry fetching the account.
   * If the account id is still not found after that, continue as if it was soft fail
   */
  useEffect(() => {
    if (isWaitingForCheckingReady && onSoftFail) {
      setIsRefetching(true)
      setTimeout(async () => {
        const { data } = await refetch()
        if (checkingAccountNotReady(data?.spendAccount)) {
          onSoftFail()
        } else {
          setIsRefetching(false)
        }
      }, RETRY_TIMEOUT)
    }
  }, [isWaitingForCheckingReady, onSoftFail, refetch])

  return {
    loading: isLoading,
  }
}
